import React, { useState, useEffect, useCallback } from 'react';
import {
  FiUserCheck, FiRefreshCw, FiSettings,
  FiPhone, FiCheckSquare, FiKey, FiArchive, FiX,
} from 'react-icons/fi';
import { listarConvertidos } from '../../../services/onboard';
import { criarCliente } from '../../../services/clientes';
import EtapasPadraoModal from './components/EtapasPadraoModal';
import DetalheModal from './components/DetalheModal';
import OnboardingTela from './components/OnboardingTela';
import styles from './Onboard.module.css';
import useEsc from '../../../hooks/useEsc';
import { nomeCurtoFluxo } from '../leads/fluxos';

const STATUS_CONFIG = {
  pendente:     { label: 'Pendentes',    dotCls: 'pendenteDot',    cardBorder: 'cardBorderPendente' },
  em_andamento: { label: 'Em Andamento', dotCls: 'emAndamentoDot', cardBorder: 'cardBorderAndamento' },
  concluido:    { label: 'Concluídos',   dotCls: 'concluidoDot',   cardBorder: 'cardBorderConcluido' },
};

const COLUNAS = ['pendente', 'em_andamento', 'concluido'];

/* ===== Card clicável ===== */

function OnboardCard({ lead, onClick }) {
  const status         = lead.onboardStatus || 'pendente';
  const cfg            = STATUS_CONFIG[status] ?? STATUS_CONFIG.pendente;
  const progresso      = lead.onboardProgresso ?? 0;
  const nomeExibido    = lead.empresa || lead.nome;
  const nomeSecundario = lead.empresa ? lead.nome : null;

  return (
    <div
      className={`${styles.card} ${styles[cfg.cardBorder]}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && onClick()}
    >
      <div className={styles.cardCorpo}>
        <p className={styles.cardNome}>{nomeExibido}</p>
        {lead.tipoFluxo && <span className={styles.cardFluxo}>{nomeCurtoFluxo(lead.tipoFluxo)}</span>}
        {nomeSecundario && <p className={styles.cardSubNome}>{nomeSecundario}</p>}
      </div>

      <div className={styles.cardRodape}>
        {lead.codigoCliente && (
          <span className={styles.cardChipCodigo}>
            <FiKey size={11} />
            {lead.codigoCliente}
          </span>
        )}
        {lead.telefone && (
          <span className={styles.cardChip}>
            <FiPhone size={11} />
            {lead.telefone}
          </span>
        )}
        {progresso > 0 && (
          <span className={styles.cardChipCheck}>
            <FiCheckSquare size={11} />
            {progresso}%
          </span>
        )}
      </div>

      <div className={styles.cardProgressoRow}>
        <div className={styles.progressoBarra}>
          <div
            className={`${styles.progressoPreenchido} ${styles['progresso_' + status]}`}
            style={{ width: `${progresso}%` }}
          />
        </div>
        <span className={styles.cardProgressoPct}>{progresso}%</span>
      </div>
    </div>
  );
}

/* ===== Coluna Kanban ===== */

function KanbanColuna({ status, leads, onCardClick }) {
  const cfg = STATUS_CONFIG[status];

  return (
    <div className={styles.kanbanColuna}>
      <div className={styles.colunaHeader}>
        <span className={`${styles.secaoDot} ${styles[cfg.dotCls]}`} />
        <span className={styles.colunaTitulo}>{cfg.label}</span>
        <span className={styles.secaoContador}>{leads.length}</span>
      </div>

      <div className={styles.colunaCards}>
        {leads.length === 0 ? (
          <div className={styles.colunaVazia}>Nenhum item</div>
        ) : (
          leads.map(lead => (
            <OnboardCard
              key={lead.id}
              lead={lead}
              onClick={() => onCardClick(lead)}
            />
          ))
        )}
      </div>
    </div>
  );
}

/* ===== Onboard anterior: a página como era (kanban + checklist por blocos),
   agora dentro de um modal de tela cheia aberto pelo botão da página.
   Mostra os leads anteriores ao fluxo novo; nada mudou no funcionamento. ===== */
function OnboardAnterior({ onFechar }) {
  useEsc(onFechar);
  const [leads, setLeads]           = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro]             = useState('');
  const [modalLead, setModalLead]   = useState(null);
  const [mostrarEtapasPadrao, setMostrarEtapasPadrao] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro('');
    try {
      setLeads(await listarConvertidos());
    } catch (e) {
      setErro(e.message);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  function handleLeadAtualizado(leadAtualizado) {
    setLeads(prev => prev.map(l => l.id === leadAtualizado.id ? leadAtualizado : l));
    setModalLead(leadAtualizado);

    /* quando onboard conclui, transfere automaticamente para clientes */
    if (
      leadAtualizado.onboardStatus === 'concluido' &&
      leadAtualizado.codigoCliente &&
      !leadAtualizado.clienteCriado &&
      !leadAtualizado.tipoFluxo
    ) {
      handleAutoTransferir(leadAtualizado);
    }
  }

  async function handleAutoTransferir(lead) {
    try {
      await criarCliente({
        codigo:  lead.codigoCliente,
        nome:    lead.empresa || lead.nome,
        empresa: lead.empresa || '',
        cnpj:    lead.cnpj   || '',
        cpf:     lead.cpf    || '',
        leadId:  lead.id,
      });
      /* remove do kanban — já está na lista de clientes */
      setLeads(prev => prev.filter(l => l.id !== lead.id));
      setModalLead(null);
    } catch (err) {
      setErro(`Erro ao transferir para clientes: ${err.message}`);
    }
  }

  const grupos = COLUNAS.reduce((acc, s) => {
    acc[s] = leads.filter(l => {
      if ((l.onboardStatus || 'pendente') !== s) return false;
      /* aqui só os anteriores ao fluxo novo; os transferidos para clientes saem */
      if (l.tipoFluxo) return false;
      if (l.clienteCriado) return false;
      return true;
    });
    return acc;
  }, {});

  const totalVisiveis = Object.values(grupos).reduce((n, arr) => n + arr.length, 0);

  return (
    <div className={styles.anteriorFundo}>
    <div className={styles.anteriorTela}>
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <h1 className={styles.titulo}>Onboard anterior <small className={styles.tituloSub}>checklist por blocos</small></h1>
        <div className={styles.headerAcoes}>
          <button
            className={styles.btnEtapasPadrao}
            onClick={() => setMostrarEtapasPadrao(true)}
          >
            <FiSettings /> Etapas Padrão
          </button>
          <button className={styles.btnAtualizar} onClick={carregar}>
            <FiRefreshCw /> Atualizar
          </button>
          <button className={styles.btnFecharAnterior} onClick={onFechar} title="Fechar"><FiX size={18} /></button>
        </div>
      </div>

      {erro && <div className={styles.erroFaixa}>{erro}</div>}

      {carregando ? (
        <p className={styles.vazio}>Carregando...</p>
      ) : totalVisiveis === 0 ? (
        <div className={styles.placeholder}>
          <FiUserCheck className={styles.placeholderIcon} />
          <p className={styles.placeholderTexto}>Nenhum cliente em onboard.</p>
          <p className={styles.placeholderSub}>
            Leads marcados como <strong>Convertido</strong> aparecem aqui automaticamente.
          </p>
        </div>
      ) : (
        <div className={styles.kanban}>
          {COLUNAS.map(s => (
            <KanbanColuna
              key={s}
              status={s}
              leads={grupos[s]}
              onCardClick={lead => setModalLead(lead)}
            />
          ))}
        </div>
      )}

      {modalLead && (
        <div style={{ position: 'relative', zIndex: 1000 }}>
          <DetalheModal
            lead={modalLead}
            onFechar={() => setModalLead(null)}
            onLeadAtualizado={handleLeadAtualizado}
          />
        </div>
      )}
      {mostrarEtapasPadrao && (
        <div style={{ position: 'relative', zIndex: 1000 }}>
          <EtapasPadraoModal onFechar={() => setMostrarEtapasPadrao(false)} />
        </div>
      )}
    </div>
    </div>
    </div>
  );
}

/* ===== Página do Onboard: o onboarding novo (leads do fluxo novo) ===== */
function Onboard() {
  const [leads, setLeads]           = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro]             = useState('');
  const [aberto, setAberto]         = useState(null);   // lead na tela de etapas
  const [checklist, setChecklist]   = useState(null);   // "Checklist anterior" de um lead
  const [anterior, setAnterior]     = useState(false);  // modal "Onboard anterior"

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro('');
    try { setLeads(await listarConvertidos()); } catch (e) { setErro(e.message); } finally { setCarregando(false); }
  }, []);
  useEffect(() => { carregar(); }, [carregar]);

  const novos = leads.filter(l => l.tipoFluxo);
  const qtdAnteriores = leads.filter(l => !l.tipoFluxo && !l.clienteCriado).length;
  const grupos = COLUNAS.reduce((acc, s) => { acc[s] = novos.filter(l => (l.onboardStatus || 'pendente') === s); return acc; }, {});

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <h1 className={styles.titulo}>Onboard</h1>
        <div className={styles.headerAcoes}>
          <button className={styles.btnEtapasPadrao} onClick={() => setAnterior(true)} title="Kanban e checklist por blocos (leads anteriores ao fluxo novo)">
            <FiArchive /> Onboard anterior ({qtdAnteriores})
          </button>
          <button className={styles.btnAtualizar} onClick={carregar}>
            <FiRefreshCw /> Atualizar
          </button>
        </div>
      </div>

      {erro && <div className={styles.erroFaixa}>{erro}</div>}

      {carregando ? (
        <p className={styles.vazio}>Carregando...</p>
      ) : novos.length === 0 ? (
        <div className={styles.placeholder}>
          <FiUserCheck className={styles.placeholderIcon} />
          <p className={styles.placeholderTexto}>Nenhum cliente no onboarding novo.</p>
          <p className={styles.placeholderSub}>
            Os leads aprovados no Leads com tipo de fluxo aparecem aqui. Os anteriores estão em <strong>Onboard anterior</strong>.
          </p>
        </div>
      ) : (
        <div className={styles.kanban}>
          {COLUNAS.map(s => (
            <KanbanColuna key={s} status={s} leads={grupos[s]} onCardClick={lead => setAberto(lead)} />
          ))}
        </div>
      )}

      {aberto && (
        <OnboardingTela leadId={aberto.id} onFechar={() => { setAberto(null); carregar(); }}
          onAbrirChecklist={() => setChecklist(aberto)} />
      )}
      {checklist && (
        /* acima da tela de etapas */
        <div style={{ position: 'relative', zIndex: 1000 }}>
          <DetalheModal lead={checklist} onFechar={() => setChecklist(null)} onLeadAtualizado={(l) => setChecklist(l)} />
        </div>
      )}
      {anterior && <OnboardAnterior onFechar={() => { setAnterior(false); carregar(); }} />}
    </div>
  );
}

export default Onboard;
